import type { MeResponse } from '@sivan/shared';
import { useEffect, useRef } from 'react';
import logoOnDark from './assets/sivan-logo-on-dark.png';
import { DefectsScreen } from './defects/DefectsScreen';
import { PortalScreen } from './defects/PortalScreen';
import { AuthGate, signOut } from './auth/AuthGate';
import { chooseProject, useCurrentProject } from './current-project';
import { hasKeptDrafts } from './handover/draft-store';
import { HandoverScreen } from './handover/HandoverScreen';
import { formatLocation } from './handover/location';
import { HomeScreen } from './home/HomeScreen';
import { MasterDataScreen } from './master-data/MasterDataScreen';
import { confirmDiscardPending } from './master-data/unsaved-changes';
import { ConfirmHost, askConfirm } from './ui/confirm';
import { useWorkspace } from './master-data/use-master-data';
import {
  PLACEHOLDERS,
  SECTIONS,
  type Section,
  type SectionId,
  navigationFor,
  railFor,
  isSupplier,
  sectionOf,
} from './navigation';
import { navigate, useHash } from './routing';
import { ApartmentWorkspace } from './apartment/ApartmentWorkspace';
import { ScheduleScreen } from './schedule/ScheduleScreen';
import { NavigatorScreen } from './structure/NavigatorScreen';
import { formatNavigatorLocation } from './structure/navigator-model';
import { TeamScreen } from './team/TeamScreen';
import { AppLink } from './ui/AppLink';
import { Breadcrumbs } from './ui/Breadcrumbs';
import { EmptyState } from './ui/EmptyState';
import { Icon } from './ui/Icon';

/** Sections whose screens work in the current project (the switcher is shown with them). */
const PROJECT_SECTIONS: readonly SectionId[] = [
  'home',
  'structure',
  'handover',
  'schedule',
  'master-data',
  'team',
];

function leave(): void {
  // Signing out removes changes kept on this device: say so instead of "kept".
  const ask = hasKeptDrafts()
    ? askConfirm({
        title: 'לצאת מהמערכת?',
        message: 'יש שינויים במסירה שעדיין לא נשמרו בשרת. יציאה מהמערכת תמחק אותם מהמכשיר הזה.',
        confirmLabel: 'יציאה ומחיקת השינויים',
        cancelLabel: 'להישאר',
        danger: true,
      })
    : confirmDiscardPending();
  void ask.then((yes) => {
    if (yes) void signOut();
  });
}

const initialsOf = (name: string) =>
  name
    .replace(/\(.*?\)/g, '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0))
    .join('');

/** The one project switcher: every screen works in the project chosen here. */
function ProjectSwitcher({ section }: { readonly section: SectionId }) {
  const [workspace] = useWorkspace();
  const projects = workspace.state === 'ready' ? workspace.data.projects : [];
  const project = useCurrentProject(projects);
  if (project === undefined || !PROJECT_SECTIONS.includes(section)) return null;
  return (
    <label className="project-switch">
      <span className="visually-hidden">פרויקט</span>
      <Icon name="building" />
      <select
        aria-label="פרויקט"
        value={project.id}
        onChange={(e) => {
          const projectId = e.target.value;
          const select = e.target;
          // The choice waits for the answer; the list shows the current project meanwhile.
          select.value = project.id;
          void confirmDiscardPending().then((switchProject) => {
            if (!switchProject) return;
            select.value = projectId;
            chooseProject(projectId);
            // The handover screens keep the project in the address: it follows the choice.
            if (section === 'handover')
              navigate(
                formatLocation({ projectId, apartmentId: null, handoverId: null }),
                'replace',
              );
            if (section === 'structure')
              navigate(
                formatNavigatorLocation({ projectId, buildingId: null, apartmentId: null }),
                'replace',
              );
          });
        }}
      >
        {projects.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name} ({p.code})
          </option>
        ))}
      </select>
    </label>
  );
}

function Account({ me, signedIn }: { readonly me: MeResponse | null; readonly signedIn: boolean }) {
  return (
    <div className="rail__account">
      {me !== null && (
        <span className="rail__who">
          <span className="rail__avatar" aria-hidden="true">
            {initialsOf(me.user.displayName)}
          </span>
          <span className="rail__name">{me.user.displayName}</span>
        </span>
      )}
      {signedIn && (
        <button type="button" className="rail__signout" onClick={leave} title="יציאה">
          <Icon name="log-out" />
          <span className="rail__label">יציאה</span>
        </button>
      )}
    </div>
  );
}

/** A section in the rail (desktop, tablet) or the tab bar (phone). */
function NavLink({
  section,
  active,
  variant,
}: {
  readonly section: Section;
  readonly active: boolean;
  readonly variant: 'rail' | 'tab';
}) {
  const text = variant === 'tab' ? (section.short ?? section.label) : section.label;
  return (
    <AppLink
      to={`#${section.id}`}
      className={variant === 'rail' ? 'rail__link' : 'tabbar__link'}
      aria-current={active ? 'page' : undefined}
      aria-label={text === section.label ? undefined : section.label}
      title={variant === 'rail' ? section.label : undefined}
    >
      <Icon name={section.icon} />
      <span className={variant === 'rail' ? 'rail__label' : undefined}>{text}</span>
    </AppLink>
  );
}

/** "עוד" on a phone: the management screens the user may open, the account, and sign-out. */
function MoreScreen({
  me,
  more,
  signedIn,
}: {
  readonly me: MeResponse | null;
  readonly more: readonly Section[];
  readonly signedIn: boolean;
}) {
  return (
    <div className="more">
      {more.length > 0 && (
        <section aria-labelledby="more-manage">
          <h2 className="more__title" id="more-manage">
            ניהול
          </h2>
          <ul className="more__list">
            {more.map((s) => (
              <li key={s.id}>
                <AppLink to={`#${s.id}`} className="more__link">
                  <Icon name={s.icon} />
                  <span>{s.label}</span>
                  <Icon name="chevron-left" />
                </AppLink>
              </li>
            ))}
          </ul>
        </section>
      )}
      <section aria-labelledby="more-account">
        <h2 className="more__title" id="more-account">
          החשבון שלי
        </h2>
        <div className="more__account">
          {me !== null && <p className="more__name">{me.user.displayName}</p>}
          <p className="hint">
            {signedIn
              ? 'מחובר/ת דרך שירות הזהות של הארגון.'
              : 'סביבת פיתוח: זהות מדומה, ללא כניסה.'}
          </p>
          {signedIn && (
            <button type="button" className="button" onClick={leave}>
              <Icon name="log-out" />
              יציאה מהמערכת
            </button>
          )}
        </div>
      </section>
    </div>
  );
}

/** An address of a module that does not exist yet: said plainly, with a way back. */
function NotAvailable({ label }: { readonly label: string }) {
  return (
    <EmptyState
      icon="clock"
      title={`${label}: המודול עדיין אינו זמין`}
      action={
        <AppLink to="#home" className="button button--primary">
          <Icon name="home" />
          לבית
        </AppLink>
      }
    >
      <p>המסך הזה טרם פותח. שום פעולה לא נשמרת כאן.</p>
    </EmptyState>
  );
}

/** Where the user is: the current project (for screens that work in one) and the section. */
function Location({ section }: { readonly section: SectionId }) {
  const [workspace] = useWorkspace();
  const projects = workspace.state === 'ready' ? workspace.data.projects : [];
  const project = useCurrentProject(projects);
  // Master Data and the apartment show their own, deeper location: one, not two.
  if (section === 'master-data' || section === 'apartment' || section === 'structure') return null;
  const label = SECTIONS[section].label;
  const items =
    project !== undefined && PROJECT_SECTIONS.includes(section) && section !== 'home'
      ? [{ label: project.name, to: '#home' }, { label }]
      : [{ label }];
  return <Breadcrumbs items={items} />;
}

export function App() {
  return (
    <>
      <AuthGate>{(state) => <Shell signedIn={state.kind === 'signed-in'} />}</AuthGate>
      <ConfirmHost />
    </>
  );
}

function Shell({ signedIn }: { readonly signedIn: boolean }) {
  // The section is the first part of the hash ("#handover/..."), so a refresh stays in it.
  const section = sectionOf(useHash());
  const [workspace] = useWorkspace();
  const me = workspace.state === 'ready' ? workspace.data.me : null;
  const nav = navigationFor(me);
  const rail = railFor(me);
  const ready = workspace.state === 'loading' ? undefined : 'true';
  const mainRef = useRef<HTMLElement>(null);
  const isFirstRender = useRef(true);
  const active = SECTIONS[section];

  // Move focus to the content after navigation so keyboard and screen-reader users land on it.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    mainRef.current?.focus();
  }, [section]);

  // A supplier's person (DR-196) sees its portal wherever it lands (its Home is the portal).
  const supplier = isSupplier(me);
  const shown: SectionId = supplier && section !== 'more' ? 'portal' : section;
  const current = (id: SectionId) => id === shown;
  const placeholder = PLACEHOLDERS.includes(section);

  return (
    <>
      <a
        className="skip-link"
        href="#main-content"
        onClick={(event) => {
          // Focus only: the hash holds the screen the user is on.
          event.preventDefault();
          mainRef.current?.focus();
        }}
      >
        דילוג לתוכן הראשי
      </a>
      <div className="app-frame">
        {/* Desktop and tablet: the sections on the right (the inline start), on graphite. */}
        <aside className="rail" aria-label="ניווט ומשתמש">
          <AppLink to="#home" className="rail__brand" title="הבית שלי">
            <img src={logoOnDark} alt="סיון ביצוע" width={670} height={160} />
          </AppLink>
          <nav className="rail__nav" aria-label="ניווט ראשי" data-ready={ready}>
            {rail.map((group) => (
              <div key={group.id} className="rail__group">
                <p className="rail__heading" id={`rail-${group.id}`}>
                  {group.label}
                </p>
                <ul aria-labelledby={`rail-${group.id}`}>
                  {group.sections.map((s) => (
                    <li key={s.id}>
                      <NavLink section={s} active={current(s.id)} variant="rail" />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
          <Account me={me} signedIn={signedIn} />
        </aside>
        <div className="app-column">
          <header className="topbar app-header">
            {/* A phone has no rail: the logo leads the top bar. */}
            <AppLink to="#home" className="topbar__brand">
              <img src={logoOnDark} alt="סיון ביצוע" width={670} height={160} />
            </AppLink>
            <Location section={section} />
            <span className="topbar__end">
              <ProjectSwitcher section={section} />
              {/* The hosted deployment is not a development environment (real accounts). */}
              {/* Only once the identity mode is known: never a flash of it while loading. */}
              {me !== null && me.identityMode !== 'supabase' && (
                <span className="env-badge" role="note">
                  <span className="env-badge__more">סביבת </span>
                  פיתוח
                  <span className="env-badge__mode">
                    {signedIn ? ' – כניסת בדיקה' : ' – זהות מדומה'}
                  </span>
                  <span className="env-badge__detail"> · נתונים סינתטיים בלבד</span>
                </span>
              )}
            </span>
          </header>
          <main
            id="main-content"
            className={`app-main app-main--${section}`}
            tabIndex={-1}
            ref={mainRef}
          >
            {/* The apartment workspace names the apartment in its own heading. */}
            {shown !== 'apartment' && shown !== 'structure' && (
              <h1 className={shown === 'home' ? 'visually-hidden' : undefined}>
                {SECTIONS[shown].label}
              </h1>
            )}
            {shown === 'portal' ? (
              <PortalScreen />
            ) : section === 'defects' ? (
              <DefectsScreen />
            ) : section === 'home' ? (
              <HomeScreen />
            ) : section === 'master-data' ? (
              <MasterDataScreen />
            ) : section === 'handover' ? (
              <HandoverScreen />
            ) : section === 'schedule' ? (
              <ScheduleScreen />
            ) : section === 'team' ? (
              <TeamScreen />
            ) : section === 'structure' ? (
              <NavigatorScreen />
            ) : section === 'apartment' ? (
              <ApartmentWorkspace />
            ) : section === 'more' ? (
              <MoreScreen me={me} more={nav.more} signedIn={signedIn} />
            ) : placeholder ? (
              <NotAvailable label={active.label} />
            ) : null}
          </main>
        </div>
      </div>
      <nav className="tabbar" aria-label="ניווט ראשי" data-ready={ready}>
        {nav.phone.map((s) => (
          <NavLink key={s.id} section={s} active={current(s.id)} variant="tab" />
        ))}
      </nav>
    </>
  );
}
